import type { FC } from 'react';
import React from 'react';

import { FileDropzone } from '@components/FileDropzone';

import { useUploadFile } from '@hooks/api/file';

import { queuePromises } from '@utils/queuePromises';

import type { PacsStudyInput } from 'generated/types';

interface DicomFileDropzoneProps {
  /**
   * ID of a diagnostic report (UUID)
   */
  diagnostic_report_id: string;
  /**
   * Callback that fires when the DICOM files finish uploading
   */
  onComplete?: (studies: PacsStudyInput[]) => Promise<void>;
}

/**
 * DICOM File dropzone component
 */
const DicomFileDropzone: FC<DicomFileDropzoneProps> = ({
  diagnostic_report_id,
  onComplete,
}) => {
  const progressRef = React.useRef(new Map<File, number>());
  const [totalBytes, setTotalBytes] = React.useState<number>(0);
  const [uploadedBytes, setUploadedBytes] = React.useState(0);

  const [{ uploadPacsInstance }] = useUploadFile();

  const onFileProgress = React.useCallback((file: File, loaded: number) => {
    setUploadedBytes((prev) => {
      const prevLoaded = progressRef.current.get(file) ?? 0;
      const delta = loaded - prevLoaded;
      progressRef.current.set(file, loaded);
      return prev + delta;
    });
  }, []);

  const handleUpload = async (files: DroppedFile[]) => {
    const uploads: PacsStudyInput[] = [];

    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    setTotalBytes(totalBytes);

    // Post each file in a queue of promises
    await queuePromises(files, async (file) => {
      return uploadPacsInstance(diagnostic_report_id, file, (eventProgress) => {
        onFileProgress(file, eventProgress.loaded);
      });
    });

    await onComplete?.(uploads);
    // Reset state
    setUploadedBytes(0);
    setTotalBytes(0);
  };

  return (
    <FileDropzone
      accept={{ ['application/zip']: ['.zip'] }}
      onUpload={handleUpload}
      progress={(uploadedBytes / totalBytes) * 100}
    />
  );
};

export default DicomFileDropzone;
